/**
 * filterTreeMove
 *
 * Relocate a node (leaf or nested group) anywhere in a canonical `FilterGroup`
 * tree by id, for the narrow-host drag-to-group interaction (EAINT-931 Phase
 * 1). Distinct from `filterGroupTree.ts`'s path-addressed `moveLeaf`, which
 * only ever moves a leaf between `SimpleFilterGroup`s in the advanced editor
 * — this operates on the canonical `FilterGroup`/`FilterGroupLeaf` shape
 * `UnifiedFilterTreeMolecule` renders, and can relocate a whole group, not
 * just a leaf.
 */
import { isPropertyLeaf } from '@center-platform/spheres/filters';
import type { FilterGroup, FilterGroupLeaf } from 'types/filters';

export interface MoveNodeOptions {
  /**
   * Narrow hosts cap group nesting at depth 1 (root → one level of groups,
   * no group-in-group). A move that would place a GROUP node at depth 2 is
   * refused; leaves are unaffected since they don't add nesting depth.
   */
  narrow?: boolean;
}

/** A leaf's stable id, at the level it carries one (see `filterGroupIds.ts`). */
const leafId = (leaf: FilterGroupLeaf): string | undefined =>
  isPropertyLeaf(leaf) ? leaf.id : leaf.filter?.id;

const isFilterGroup = (node: FilterGroupLeaf | FilterGroup): node is FilterGroup =>
  'expressions' in node;

interface LocatedLeaf {
  kind: 'leaf';
  leaf: FilterGroupLeaf;
  parent: FilterGroup;
  depth: number;
}
interface LocatedGroup {
  kind: 'group';
  group: FilterGroup;
  parent: FilterGroup | null;
  depth: number;
}
type Located = LocatedLeaf | LocatedGroup;

function locate(
  tree: FilterGroup,
  nodeId: string,
  parent: FilterGroup | null = null,
  depth = 0
): Located | undefined {
  if (tree.id === nodeId) return { kind: 'group', group: tree, parent, depth };
  const leaf = tree.expressions.find((expr) => leafId(expr) === nodeId);
  if (leaf) return { kind: 'leaf', leaf, parent: tree, depth };
  for (const child of tree.groups ?? []) {
    const found = locate(child, nodeId, tree, depth + 1);
    if (found) return found;
  }
  return undefined;
}

/** True when `groupId` names `ancestor` itself or any group nested under it. */
function isSelfOrDescendant(ancestor: FilterGroup, groupId: string): boolean {
  if (ancestor.id === groupId) return true;
  return (ancestor.groups ?? []).some((g) => isSelfOrDescendant(g, groupId));
}

/**
 * Whether `nodeId` may be relocated into the group `targetGroupId`: both
 * must exist, the target must actually be a group, the move must not create
 * a cycle (a group can't move into itself or its own descendant), and in a
 * narrow host a group can't move into an already-nested (depth ≥ 1) target.
 */
export function canMoveInto(
  tree: FilterGroup,
  nodeId: string,
  targetGroupId: string,
  options: MoveNodeOptions = {}
): boolean {
  if (nodeId === targetGroupId) return false;
  const node = locate(tree, nodeId);
  const target = locate(tree, targetGroupId);
  if (!node || target?.kind !== 'group') return false;

  if (node.kind === 'group') {
    if (isSelfOrDescendant(node.group, targetGroupId)) return false;
    if (options.narrow && target.depth >= 1) return false;
  }
  return true;
}

function removeNode(group: FilterGroup, nodeId: string): FilterGroup {
  const nextExpressions = group.expressions.filter((leaf) => leafId(leaf) !== nodeId);
  if (!group.groups) return { ...group, expressions: nextExpressions };
  return {
    ...group,
    expressions: nextExpressions,
    groups: group.groups.filter((g) => g.id !== nodeId).map((g) => removeNode(g, nodeId)),
  };
}

function insertNode(
  group: FilterGroup,
  targetGroupId: string,
  node: FilterGroupLeaf | FilterGroup
): FilterGroup {
  if (group.id === targetGroupId) {
    return isFilterGroup(node)
      ? { ...group, groups: [...(group.groups ?? []), node] }
      : { ...group, expressions: [...group.expressions, node] };
  }
  if (!group.groups) return group;
  return { ...group, groups: group.groups.map((g) => insertNode(g, targetGroupId, node)) };
}

/**
 * Relocate the node `nodeId` (a leaf or a nested group) into the group
 * `targetGroupId`, appending it as the last child. `targetGroupId` may name
 * the root itself. Returns `tree` unchanged (same reference) when the node
 * is already a child of the target, or when `canMoveInto` refuses the move.
 */
export function moveNode(
  tree: FilterGroup,
  nodeId: string,
  targetGroupId: string,
  options: MoveNodeOptions = {}
): FilterGroup {
  const node = locate(tree, nodeId);
  if (!node) return tree;

  const currentParentId = node.parent?.id;
  if (currentParentId === targetGroupId) return tree;
  if (!canMoveInto(tree, nodeId, targetGroupId, options)) return tree;

  const nodeValue: FilterGroupLeaf | FilterGroup = node.kind === 'leaf' ? node.leaf : node.group;
  const withoutNode = removeNode(tree, nodeId);
  return insertNode(withoutNode, targetGroupId, nodeValue);
}

/**
 * Every group id in the tree, root first, in pre-order (a group before its own
 * nested groups) — the keyboard drag-to-group cursor's traversal order
 * (`UnifiedFilterTreeMolecule`'s `useLiftAndMove` in `'preview'` mode). A group
 * with no `id` yet (authored outside this tree) is skipped — it can't be
 * addressed as a drop target either way.
 */
export function listGroupIds(tree: FilterGroup): string[] {
  if (!tree.id) return (tree.groups ?? []).flatMap(listGroupIds);
  return [tree.id, ...(tree.groups ?? []).flatMap(listGroupIds)];
}

/**
 * Append `leaf` to the group `targetGroupId` (root or any nested group),
 * for the narrow-host drill-in "Add a condition … to this filter / to the
 * group" container (`UnifiedFilterTreeMolecule`). Returns `tree` unchanged
 * when `targetGroupId` names no group in the tree.
 */
export function addLeafToGroup(
  tree: FilterGroup,
  targetGroupId: string,
  leaf: FilterGroupLeaf
): FilterGroup {
  return insertNode(tree, targetGroupId, leaf);
}
